<script setup lang="ts">
import type {JournalEntryDTO} from '@/ts/types/dto/journals-dto';
import FeelingChip from '@/vue/molecules/feeling-chip.vue';
import MoodFace from '@/vue/atoms/mood-face.vue';
import BadgePill from '@/vue/atoms/badge-pill.vue';
import FormattedDate from '@/vue/atoms/formatted-date.vue';
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {useMoodLevel} from '@/ts/composables/pure/use-mood-level';
import {useScreenSize} from '@/ts/composables/stateful/use-screen-size';
import {ScreenSize} from '@/ts/types/component/screen-size.type';

const MAX_FEELINGS_MOBILE = 3;
const MAX_FEELINGS_TABLET = 4;
const MAX_FEELINGS_DESKTOP_LARGE = 4;
const MAX_FEELINGS_DESKTOP_XLARGE = 5;

const props = defineProps<{
  entry: JournalEntryDTO;
}>();

const emit = defineEmits<{
  click: [];
}>();

const {t} = useI18n();
const {getMoodLevelLabel, getMoodLevelImage} = useMoodLevel();
const {screenSize} = useScreenSize();

const hasMoodLevel = computed(() => Boolean(props.entry.moodLevel));

const moodLevelLabel = computed(() => {
  if (!props.entry.moodLevel) {
    return '';
  }
  return getMoodLevelLabel(props.entry.moodLevel);
});

const moodTitle = computed(() => {
  if (!props.entry.moodLevel) {
    return '';
  }
  return t('feeling_level', {level: getMoodLevelLabel(props.entry.moodLevel)});
});

/**
 * Entries written with the 10-mood vocabulary (IPD-2872). Legacy entries keep their own smiley
 * and label below rather than being mapped onto a new mood — on this tab an old entry stays the
 * entry the user actually wrote (IPD-3107), which is why `getMoodForLegacyLevel` is deliberately
 * not used here even though the Today tile's card stack does apply it.
 */
const hasMood = computed(() => Boolean(props.entry.mood));

const moodName = computed(() => (props.entry.mood ? t(`mood_${props.entry.mood}`) : ''));

const allFeelings = computed(() => {
  const feelings: string[] = [];
  if (props.entry.primaryFeelings) {
    feelings.push(...props.entry.primaryFeelings.map(f => f.name));
  }
  if (props.entry.secondaryFeelings) {
    feelings.push(...props.entry.secondaryFeelings.map(f => f.name));
  }
  return feelings;
});

const hasFeelings = computed(() => allFeelings.value.length > 0);

const maxVisible = computed(() => {
  if ([ScreenSize.XL, ScreenSize.XXL].includes(screenSize.value)) {
    return MAX_FEELINGS_DESKTOP_XLARGE;
  }
  if (screenSize.value === ScreenSize.LG) {
    return MAX_FEELINGS_DESKTOP_LARGE;
  }
  if (screenSize.value === ScreenSize.MD) {
    return MAX_FEELINGS_TABLET;
  }
  return MAX_FEELINGS_MOBILE;
});

const effectiveMax = computed(() => {
  const remaining = allFeelings.value.length - maxVisible.value;
  // If only 1 would be hidden, show it instead of "+1 more"
  return remaining === 1 ? maxVisible.value + 1 : maxVisible.value;
});

const visibleFeelings = computed(() => allFeelings.value.slice(0, effectiveMax.value));
const overflowCount = computed(() => Math.max(0, allFeelings.value.length - effectiveMax.value));
</script>

<template>
  <button type="button" class="journal-entry-card" @click="emit('click')">
    <div class="card-content">
      <div class="left-block">
        <div v-if="hasMood" class="mood-info">
          <mood-face :mood="entry.mood!" :size="24" tinted/>
          <span class="mood-name">{{ moodName }}</span>
        </div>
        <div v-else-if="hasMoodLevel" class="mood-level-info">
          <img
            :src="getMoodLevelImage(entry.moodLevel!)"
            :alt="moodLevelLabel"
            class="mood-level-image"
          />
          <span class="mood-level-name">{{ moodTitle }}</span>
        </div>
        <h4 v-else class="entry-name">
          {{ entry.name }}
        </h4>
        <p v-if="entry.answer" class="entry-answer">
          {{ entry.answer }}
        </p>
        <div class="entry-meta">
          <formatted-date
            v-if="entry.createdAt"
            :date="entry.createdAt"
            format="relativeDateTime"
            class="entry-date"
          />
          <div v-if="entry.interventionName" class="intervention-container">
            <span class="part-of-label">{{ $t('part_of') }}</span>
            <badge-pill theme="light" :label="entry.interventionName"/>
          </div>
        </div>
      </div>
      <div v-if="hasFeelings" class="right-block">
        <div class="feelings-container">
          <feeling-chip
            v-for="feeling in visibleFeelings"
            :key="feeling"
            :name="feeling"
          />
          <span v-if="overflowCount > 0" class="feelings-overflow-chip">
            {{ $t('n_more_feelings', {n: overflowCount}) }}
          </span>
        </div>
      </div>
    </div>
  </button>
</template>

<style lang="scss" scoped>
.journal-entry-card {
  border-radius: var(--card-border-radius);
  background: var(--Neutral-UI-neutral-ui-10);
  // A plain card: no accent stripe down the side. The design has none, and the one that was here
  // spent the primary colour on every row of a list where nothing distinguishes one row from
  // another.
  border: none;
  padding: var(--Spacing-spacing-6);
  text-align: left;
  width: 100%;
  font: inherit;
  cursor: pointer;
  // The card darkens a step under the pointer and nothing else moves: no lift, no shadow, no
  // outline. Curve and duration are the design system's own card hover rather than the `ease` the
  // Today tiles use — on a 200ms colour fade the two are indistinguishable, and this is the one
  // the reference actually ships.
  transition: background 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  @include focus-visible;

  &:hover {
    background: color-mix(in srgb, var(--Neutral-UI-neutral-ui-10) 88%, var(--Neutral-UI-neutral-ui-100));
  }
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);

  @include screen-md {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--Spacing-spacing-5);
  }
}

.left-block {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-2);
  flex: 1 1 50%;
  min-width: 0;

  @include screen-md {
    max-width: 600px;
  }
}

.mood-info,
.mood-level-info {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  padding-bottom: var(--Spacing-spacing-2);
}

.mood-name {
  @include font-body-bold;
  color: var(--Neutral-UI-neutral-ui-100);
  padding-top: 4px;
}

.mood-level-image {
  display: block;
  width: 24px;
  height: 24px;
}

.mood-level-name {
  @include font-body-bold;
  color: var(--Neutral-UI-neutral-ui-100);
  padding-top: 4px;
}

.entry-name {
  @include font-body-bold;
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;
}

.entry-answer {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;
  margin-bottom: var(--Spacing-spacing-2);
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 3;

  @include screen-md {
    -webkit-line-clamp: 2;
  }
}

.entry-meta {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-4);
  flex-wrap: wrap;
}

.entry-date {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-60);
}

.intervention-container {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-2);
}

.part-of-label {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-60);
}

.right-block {
  flex: 0 1 50%;
  min-width: 0;
}

.feelings-container {
  display: flex;
  flex-wrap: wrap;
  gap: var(--Spacing-spacing-2);

  @include screen-md {
    justify-content: flex-end;
  }

  :deep(.feeling-chip) {
    background-color: var(--Neutral-UI-neutral-ui-20);
    color: var(--Neutral-UI-neutral-ui-90);
    @include font-body-sm-bold;
  }
}

.feelings-overflow-chip {
  @include font-body-sm-bold;
  color: var(--Neutral-UI-neutral-ui-60);
  background-color: var(--Neutral-UI-neutral-ui-20);
  border-radius: var(--button-border-radius);
  padding: 0 var(--Spacing-spacing-3);
  height: 30px;
  display: flex;
  align-items: center;
  white-space: nowrap;

  @include screen-lg {
    height: 40px;
  }
}
</style>
